import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BusinessVertical } from "@graftus/schema";

/**
 * The sector word pack (onboarding `sector.words.<sector>`): every noun the
 * onboarding adapts to the kind of organisation. Components read words from
 * here and never write a sector noun as a literal, so the copy follows the
 * sector and the language.
 */
export interface SectorWords {
  /** "avdelning" | "säljteam" */
  team: string;
  teamCap: string;
  teamPluralDef: string;
  /** "Butiken" | "Företaget" */
  unit: string;
  nameL: string;
  namePh: string;
  nrL: string;
  nrPh: string;
  /** Label of the chain / sales-type chips. */
  chainL: string;
  /** Chip options keyed by the stored enum value. */
  choices: Record<string, string>;
  chefDef: string;
  adminOf: string;
  domain: string;
  deptEx: string;
  roleEx: string;
  source: string;
  compare: string;
}

export function useSectorWords(sector: BusinessVertical | null | undefined): SectorWords {
  const { t, i18n } = useTranslation("onboarding");
  const key = sector ?? "sales";
  return useMemo(
    () => t(`sector.words.${key}`, { returnObjects: true }) as SectorWords,
    // The language is part of the key's value even though not of the key itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, key, i18n.language],
  );
}
